import Service from '@/pages/dashboard/components/PageContainer/Service';
import type { SeriesConfig } from '@/pages/dashboard/components/EventMetricTrend/types';
import { pickKpiOverviewAnomalyLayer } from '../AnomalyService';
import React, { useContext, useMemo } from 'react';
import { useSelector, shallowEqual } from 'umi';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import {
  getUaRoiUaMediaScopeSelectorFingerprint,
  mergeUaRoiUaMediaScopeIntoFormattedFilters,
  isUaRoiUaMediaScopeNoData,
  type TKpiOverviewDataScope,
} from '../../utils/uaRoiUaMediaScopeFilters';
import { DEFAULT_API, DEFAULT_TRACK_DATA } from './_type';
import {
  buildAnomalyScatterSeries,
  filterAnomalyByMetricKey,
  kpiMetricsTrendBeforeRequest,
  normalizeMetricsTrendResponse,
  shouldApplyAnomalyMerge,
} from './_utils';
import {
  useKpiEventMetricGameCode,
  useKpiEventMetricMergedData,
  useKpiEventMetricServicePayload,
} from './_hooks';

/** KpiEventMetricTrendInner props：Service 内层组件接收 Service 注入的 data 及上层透传的异动/事件数据 */
interface IKpiEventMetricTrendInnerProps {
  children?: React.ReactNode;
  eventData?: unknown;
  loading?: unknown;
  anomalyResult?: unknown;
  anomalyResultError?: unknown;
  anomalyResultLoading?: unknown;
  mergeAnomalyIntoTrend?: boolean;
  anomalyMetricKey?: string;
  /** 与 KpiEventMetricTrendService / UA Tab 对齐，用于选取异动 `all` 或 `uaroi` 分层 */
  kpiOverviewDataScope?: TKpiOverviewDataScope;
  /** 与 KpiEventDimensionTrend 一致，插入在标题与图表之间的区域（如 Segment） */
  contentHeader?: React.ReactNode;
  // Service 注入：{ trendData: {...} } 等
  data?: { trendData?: Record<string, unknown>; [key: string]: unknown; };
  // 其他 props 透传到 cloneElement
  [key: string]: unknown;
}

const KpiEventMetricTrendInner = (props: IKpiEventMetricTrendInnerProps) => {
  const {
    children,
    eventData: rawEventList,
    loading: eventListLoading,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    mergeAnomalyIntoTrend,
    anomalyMetricKey,
    contentHeader,
    kpiOverviewDataScope = 'all',
    data: serviceData,
    ...rest
  } = props;

  const trendState = serviceData?.trendData;

  const mergedData = useKpiEventMetricMergedData({
    serviceData,
    trendState,
    rawEventList,
    eventListLoading,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    mergeAnomalyIntoTrend,
    anomalyMetricKey,
    kpiOverviewDataScope,
  });

  const childSeriesConfig = useMemo(() => {
    // React.Children.toArray 返回的元素在有效时是 ReactElement，props 形态由业务约定
    const firstChild = React.Children.toArray(children)?.[0] as
      | React.ReactElement<{
        seriesConfig?: SeriesConfig[];
        config?: { props?: { seriesConfig?: SeriesConfig[]; }; };
      }>
      | undefined;
    if (!firstChild) return undefined;
    return (
      firstChild?.props?.seriesConfig ??
      firstChild?.props?.config?.props?.seriesConfig
    ) as SeriesConfig[] | undefined;
  }, [children]);

  const metricAnomalyScatterConfigs = useMemo(() => {
    if (!childSeriesConfig?.length || !anomalyResult || !anomalyMetricKey) {
      return [];
    }
    const filteredAnomaly = filterAnomalyByMetricKey(
      pickKpiOverviewAnomalyLayer(anomalyResult, kpiOverviewDataScope),
      anomalyMetricKey,
    );
    if (!shouldApplyAnomalyMerge(filteredAnomaly, anomalyResultError)) {
      return [];
    }
    const sd = (mergedData as { trendData?: { data?: { seriesData?: Record<string, unknown>[]; }; }; })
      ?.trendData?.data?.seriesData;
    if (!Array.isArray(sd) || sd.length === 0) {
      return [];
    }
    return buildAnomalyScatterSeries(
      filteredAnomaly,
      anomalyMetricKey,
      sd,
      childSeriesConfig,
    ).anomalySeriesConfigs;
  }, [
    childSeriesConfig,
    anomalyResult,
    anomalyResultError,
    anomalyMetricKey,
    kpiOverviewDataScope,
    mergedData,
  ]);

  const enrichedData = useMemo(() => {
    if (!mergedData || !metricAnomalyScatterConfigs?.length) return mergedData;
    return {
      ...mergedData,
      _anomalyScatterConfigs: metricAnomalyScatterConfigs,
    };
  }, [mergedData, metricAnomalyScatterConfigs]);

  return (
    <>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return child;
        }
        const injectedProps: React.Attributes & Record<string, unknown> = {
          ...rest,
          data: enrichedData,
          ...(contentHeader !== undefined ? { contentHeader } : {}),
        };
        return React.cloneElement(child, injectedProps);
      })}
    </>
  );
};

/** KpiEventMetricTrendService props：外层适配组件，接受 config/api/trackData 等 */
interface IKpiEventMetricTrendServiceProps {
  style?: React.CSSProperties;
  className?: string;
  // config 由注册表注入，结构灵活
  config?: Record<string, unknown> & {
    mergeAnomalyIntoTrend?: boolean;
    anomalyMetricKey?: string;
    minTrendRangeDays?: number;
    api?: string | ((data: unknown, outParame?: unknown) => void);
    dataKey?: string;
    trackData?: readonly string[];
    beforeRequest?: string | ((data: unknown) => unknown);
    formatData?: string | ((data: unknown, filterVal?: unknown) => unknown);
    metrics?: unknown[];
    contentHeader?: React.ReactNode;
    applyKpiUaRoiUaMediaScope?: boolean;
    kpiOverviewDataScope?: TKpiOverviewDataScope;
  };
  children?: React.ReactNode;
  data?: Record<string, unknown>;
  eventData?: unknown;
  loading?: unknown;
  anomalyResult?: unknown;
  anomalyResultError?: unknown;
  anomalyResultLoading?: unknown;
  api?: string | ((data: unknown, outParame?: unknown) => void);
  dataKey?: string;
  trackData?: readonly string[];
  beforeRequest?: string | ((data: unknown) => unknown);
  formatData?: string | ((data: unknown, filterVal?: unknown) => unknown);
  metrics?: unknown[];
  mergeAnomalyIntoTrend?: boolean;
  anomalyMetricKey?: string;
  minTrendRangeDays?: number;
  contentHeader?: React.ReactNode;
  /** UA 团队区块：在「UA-ROI Campaign & UA Media Channel」口径下收敛 campaign / ua_network */
  applyKpiUaRoiUaMediaScope?: boolean;
  /** 可选覆盖；默认取自 props.data.kpiOverviewDataScope（KpiOverviewScopeTabs cloneElement 注入） */
  kpiOverviewDataScope?: TKpiOverviewDataScope;
}

const KpiEventMetricTrendService = (props: IKpiEventMetricTrendServiceProps) => {
  const {
    style,
    className,
    config,
    children,
    data: propsData,
    eventData: rawEventList,
    loading: eventListLoading,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    api: apiProp,
    dataKey: dataKeyProp,
    trackData: trackDataProp,
    beforeRequest: beforeRequestProp,
    formatData: formatDataProp,
    metrics: metricsProp,
    mergeAnomalyIntoTrend: mergeAnomalyIntoTrendProp,
    anomalyMetricKey: anomalyMetricKeyProp,
    minTrendRangeDays: minTrendRangeDaysProp,
    contentHeader: contentHeaderProp,
    applyKpiUaRoiUaMediaScope: applyKpiUaRoiUaMediaScopeProp,
    kpiOverviewDataScope: kpiOverviewDataScopeProp,
  } = props || {};
  const mergeAnomalyIntoTrend =
    mergeAnomalyIntoTrendProp ?? config?.mergeAnomalyIntoTrend ?? true;
  const anomalyMetricKey =
    anomalyMetricKeyProp ?? config?.anomalyMetricKey;
  const minTrendRangeDays =
    minTrendRangeDaysProp ?? config?.minTrendRangeDays;
  const contentHeader =
    contentHeaderProp ?? config?.contentHeader;

  const applyKpiUaRoiUaMediaScope =
    applyKpiUaRoiUaMediaScopeProp ?? config?.applyKpiUaRoiUaMediaScope;

  const kpiOverviewScope =
    kpiOverviewDataScopeProp ??
    (config?.kpiOverviewDataScope as TKpiOverviewDataScope | undefined) ??
    (propsData as { kpiOverviewDataScope?: TKpiOverviewDataScope; } | undefined)
      ?.kpiOverviewDataScope ??
    'all';

  const { selector: uaScopeSelector } = useSelector(
    (state: { dashboardSetting?: { selector?: Record<string, unknown>; }; }) => ({
      selector: state?.dashboardSetting?.selector as
        | Record<string, Array<Record<string, unknown>>>
        | undefined,
    }),
    shallowEqual,
  );

  const api = apiProp ?? config?.api ?? DEFAULT_API;
  const dataKey = dataKeyProp ?? config?.dataKey ?? 'trendData';
  const trackData = useMemo(() => {
    const base = (trackDataProp ?? config?.trackData ?? [...DEFAULT_TRACK_DATA]) as string[];
    if (applyKpiUaRoiUaMediaScope) {
      return [...base, '_kpiOverviewScopeToken'];
    }
    return base;
  }, [trackDataProp, config?.trackData, applyKpiUaRoiUaMediaScope]);
  const rawBeforeRequest =
    beforeRequestProp ?? config?.beforeRequest ?? kpiMetricsTrendBeforeRequest;

  const beforeRequest = useMemo(() => {
    if (!applyKpiUaRoiUaMediaScope) {
      return rawBeforeRequest;
    }
    return (reqData: Record<string, unknown>) => {
      let baseFn: (d: Record<string, unknown>) => Record<string, unknown> | false;
      if (typeof rawBeforeRequest === 'string') {
        baseFn = stringToFunction(rawBeforeRequest) as typeof baseFn;
      } else if (typeof rawBeforeRequest === 'function') {
        baseFn = rawBeforeRequest as typeof baseFn;
      } else {
        baseFn = kpiMetricsTrendBeforeRequest as typeof baseFn;
      }
      const base = baseFn(reqData);
      if (base === false) {
        return false;
      }
      if (kpiOverviewScope !== 'ua_roi_ua_media') {
        return base;
      }
      const filters = base.filters as Record<string, unknown> | undefined;
      if (!filters || typeof filters !== 'object') {
        return base;
      }
      const merged = mergeUaRoiUaMediaScopeIntoFormattedFilters(
        filters,
        reqData.filterVal as Record<string, unknown> | undefined,
        uaScopeSelector,
      );
      if (merged === false) {
        return false;
      }
      return { ...base, filters: merged };
    };
  }, [
    applyKpiUaRoiUaMediaScope,
    kpiOverviewScope,
    rawBeforeRequest,
    uaScopeSelector,
  ]);

  const { gameCode } = useKpiEventMetricGameCode();
  const metrics = metricsProp ?? config?.metrics ?? [];
  const formatData = useMemo(() => {
    if (formatDataProp) {
      return formatDataProp;
    }
    if (config?.formatData) {
      return config.formatData;
    }
    const m = Array.isArray(metrics) ? metrics : [];
    // raw 为后端原始响应（可能是 object 或数组），normalizeMetricsTrendResponse 内部有类型兜底
    return (raw: unknown) => normalizeMetricsTrendResponse(
      raw as Parameters<typeof normalizeMetricsTrendResponse>[0],
      m as Parameters<typeof normalizeMetricsTrendResponse>[1],
    );
  }, [formatDataProp, config?.formatData, metrics]);
  const data = useKpiEventMetricServicePayload(gameCode, {
    ...(propsData || {}),
    metrics: Array.isArray(metrics) ? metrics : [],
    ...(typeof minTrendRangeDays === 'number' ? { minTrendRangeDays } : {}),
    ...(applyKpiUaRoiUaMediaScope
      ? { _kpiOverviewScopeToken: kpiOverviewScope }
      : {}),
  });

  /** 不改动底层 Service：口径或 UA selector 变化时换 key，避免 Service 内 getApiData 闭包仍引用旧 beforeRequest */
  const serviceMountKey = useMemo(() => {
    if (!applyKpiUaRoiUaMediaScope) {
      return 'kpi-event-metric-trend';
    }
    if (kpiOverviewScope !== 'ua_roi_ua_media') {
      return `kpi-event-metric-trend-${kpiOverviewScope}`;
    }
    return `kpi-event-metric-trend-${kpiOverviewScope}-${getUaRoiUaMediaScopeSelectorFingerprint(
      uaScopeSelector,
    )}`;
  }, [applyKpiUaRoiUaMediaScope, kpiOverviewScope, uaScopeSelector]);

  /** UA 口径下筛选器不匹配时短路：跳过 Service 请求，直接展示无数据 */
  const { filterVal: ctxFilterVal } = useContext(FilterContext);
  const isScopeNoData = useMemo(() => {
    if (!applyKpiUaRoiUaMediaScope || kpiOverviewScope !== 'ua_roi_ua_media') {
      return false;
    }
    return isUaRoiUaMediaScopeNoData(
      ctxFilterVal as Record<string, unknown> | undefined,
      uaScopeSelector,
    );
  }, [applyKpiUaRoiUaMediaScope, kpiOverviewScope, ctxFilterVal, uaScopeSelector]);

  /** 短路无数据时直接渲染子组件（传入空数据 + loading: false） */
  const noDataTrendData = useMemo(
    () => ({ trendData: { data: null, loading: false }, loading: false } satisfies Record<string, unknown>),
    [],
  );

  if (isScopeNoData) {
    return (
      <div className={className} style={style}>
        <KpiEventMetricTrendInner
          data={noDataTrendData}
          eventData={rawEventList}
          loading={eventListLoading}
          anomalyResult={anomalyResult}
          anomalyResultError={anomalyResultError}
          anomalyResultLoading={anomalyResultLoading}
          mergeAnomalyIntoTrend={mergeAnomalyIntoTrend}
          anomalyMetricKey={anomalyMetricKey}
          kpiOverviewDataScope={kpiOverviewScope}
          contentHeader={contentHeader}
        >
          {children}
        </KpiEventMetricTrendInner>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <Service
        key={serviceMountKey}
        api={api as string | ((data: unknown, outParame?: unknown) => void)}
        data={data}
        dataKey={dataKey}
        trackData={trackData as string[]}
        beforeRequest={beforeRequest as string | ((data: unknown) => unknown)}
        formatData={formatData as string | ((data: unknown, filterVal?: unknown) => unknown)}
      >
        <KpiEventMetricTrendInner
          eventData={rawEventList}
          loading={eventListLoading}
          anomalyResult={anomalyResult}
          anomalyResultError={anomalyResultError}
          anomalyResultLoading={anomalyResultLoading}
          mergeAnomalyIntoTrend={mergeAnomalyIntoTrend}
          anomalyMetricKey={anomalyMetricKey}
          kpiOverviewDataScope={kpiOverviewScope}
          contentHeader={contentHeader}
        >
          {children}
        </KpiEventMetricTrendInner>
      </Service>
    </div>
  );
};

export default KpiEventMetricTrendService;
